import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import TrainingCatalogTable from './components/TrainingCatalogTable';
import useTrainingCatalogData from './hooks/useTrainingCatalogData';
import GenericTableFilter from '../../../components/common/GenericTableFilter';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useLmsPermission } from '../hooks/useLmsPermission';
import { LMS } from '../../../constants/permissionCodes';
import useLmsCodeOptions from '../hooks/useLmsCodeOptions';

/**
 * Eğitim Kataloğu ana sayfası — liste ve filtre. Ekleme/düzenleme artık modal değil, ayrı bir sayfa
 * (`createEditForm/TrainingCatalogCreateEdit.jsx`, route: `training-catalog/create` ve
 * `training-catalog/:id`) — eğitmenler / içerik-kazanım / ön koşullar detay sekmeleri de o sayfanın
 * alt kısmına taşındı, bkz. docs/LMS-Egitim-Katalogu-ve-Egitmenler-API 1.md.
 */
const TrainingCatalogPage = () => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const { can } = useLmsPermission();

    const [filterResetKey, setFilterResetKey] = useState(0);

    const { trainingCatalogApi, isLoading, lazyParams, onPage, activeFilters, handleFilterChange, initFilters } = useTrainingCatalogData();

    const { data: trainingTypesData, options: trainingTypeOptions } = useLmsCodeOptions('TRAINING_TYPES');
    const { data: trainingLocationsData, options: trainingLocationOptions } = useLmsCodeOptions('TRAINING_LOCATION');
    const { data: vehicleModelsData, options: vehicleModelOptions } = useLmsCodeOptions('VEHICLE_MODEL');

    // Tablo, vehicleModel alanında (backend'in ayrıca bir "vehicleModelText" alanı döndürmediği için)
    // ham `code` yerine okunabilir metni göstersin diye code->text eşlemesi burada üretiliyor.
    // Bu lookup'tan önce girilmiş eski (serbest metin) kayıtlarda eşleşme bulunamazsa ham değer gösterilir.
    const vehicleModelTextByCode = useMemo(() => {
        const map = {};
        (vehicleModelsData?.items || []).forEach((item) => {
            map[item.code] = item.text;
        });
        return map;
    }, [vehicleModelsData]);

    const filterFields = useMemo(
        () => [
            { field: 'trainingCode', headerName: th('trainingCatalog.columnCode'), type: 'text' },
            { field: 'trainingName', headerName: th('trainingCatalog.columnName'), type: 'text' },
            {
                field: 'vehicleModel',
                headerName: th('trainingCatalog.columnVehicleModel'),
                type: 'dropdown',
                options: vehicleModelOptions,
                optionLabel: 'label',
                optionValue: 'value'
            },
            {
                field: 'trainingType',
                headerName: th('trainingCatalog.columnTrainingType'),
                type: 'dropdown',
                options: trainingTypeOptions,
                optionLabel: 'label',
                optionValue: 'value'
            },
            {
                field: 'trainingLocation',
                headerName: th('trainingCatalog.columnTrainingLocation'),
                type: 'dropdown',
                options: trainingLocationOptions,
                optionLabel: 'label',
                optionValue: 'value'
            },
            {
                field: 'isActive',
                headerName: th('common.status'),
                type: 'dropdown',
                options: [
                    { value: 1, label: th('common.active', { fallback: 'Aktif' }) },
                    { value: 0, label: th('common.inactive', { fallback: 'Pasif' }) }
                ],
                optionLabel: 'label',
                optionValue: 'value'
            }
        ],
        [th, trainingTypeOptions, trainingLocationOptions, vehicleModelOptions]
    );

    const onApplyFilters = (uiFilters) => {
        handleFilterChange(uiFilters);
    };

    const onResetFilters = () => {
        initFilters();
        setFilterResetKey((prev) => prev + 1);
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('trainingCatalog.title')}</h1>
                {can(LMS.TRAINING_CREATE) && <Button label={th('trainingCatalog.newButton')} icon="pi pi-plus" onClick={() => navigate('/learning-management-system/training-catalog/create')} />}
            </div>

            <div className="mb-2">
                <GenericTableFilter
                    key={`training-catalog-filter-${filterResetKey}`}
                    filterFields={filterFields}
                    initialFilters={{
                        trainingCode: activeFilters?.trainingCode ?? '',
                        trainingName: activeFilters?.trainingName ?? '',
                        vehicleModel: activeFilters?.vehicleModel ?? '',
                        trainingType: activeFilters?.trainingType ?? '',
                        trainingLocation: activeFilters?.trainingLocation ?? '',
                        isActive: activeFilters?.isActive ?? null
                    }}
                    onSearch={() => {}}
                    onApplyFilters={onApplyFilters}
                    onResetFilters={onResetFilters}
                />
            </div>

            <TrainingCatalogTable data={trainingCatalogApi} totalCount={trainingCatalogApi?.totalCount} loading={isLoading} lazyParams={lazyParams} onPage={onPage} vehicleModelTextByCode={vehicleModelTextByCode} />
        </div>
    );
};

export default TrainingCatalogPage;
